/**
 * DomainSection - Display equipment for a single domain
 * Renders required items, wizard-selected items, and optional items for one domain
 */

import React from 'react';
import { CheckCircle, Circle, AlertCircle, Lock } from 'lucide-react';
import { Switch } from '../../../../catalyst/checkbox';
import QuantityControl from './QuantityControl';
// Abstract requirements removed - all choices made in Step 5

const DomainSection = ({
  domain,
  domainData,
  domainLabel,
  onRequiredItemClick,
  onItemToggle,
  onQuantityChange
}) => {
  const requiredCount = domainData.required?.length || 0;
  const optionalCount = domainData.optional?.length || 0;
  const wizardSelectedCount = domainData.wizardSelected?.length || 0;
  const optionalSelected = (domainData.optional || []).filter(item => item.selected).length;
  
  if (requiredCount === 0 && optionalCount === 0 && wizardSelectedCount === 0) {
    return null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Domain Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {domainLabel}
          </h3>
          <div className="text-sm text-gray-600">
            {requiredCount} required{wizardSelectedCount > 0 && `, ${wizardSelectedCount} wizard-selected`}{optionalCount > 0 && `, ${optionalSelected}/${optionalCount} optional`}
          </div>
        </div>
      </div>

      {/* Equipment Items */}
      <div className="divide-y divide-gray-200">
        {/* Required Items */}
        {domainData.required?.map(item => (
          <div key={item.id} className="px-6 py-4 flex items-center">
            <div className="flex items-center">
              <Switch
                checked={item.presence !== 'overridden'}
                onChange={(checked) => {
                  if (!checked) {
                    // Trying to untoggle a required item - trigger override modal
                    onRequiredItemClick(domain, item);
                  }
                  // If toggling back on, we'd need to clear the override
                  // For now, just trigger the override modal for all required item changes
                }}
                className="mr-3"
                disabled={item.presence === 'overridden'} // Prevent changes to overridden items via toggle
              />
              {item.presence === 'overridden' && (
                <button
                  onClick={() => onRequiredItemClick(domain, item)}
                  className="text-amber-600 hover:text-amber-700"
                  title="Click to modify override"
                >
                  <AlertCircle className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-gray-900 font-medium">{item.name}</span>
                  {item.presence === 'overridden' ? (
                    <div className="ml-2 flex items-center space-x-2">
                      <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded">
                        OVERRIDDEN
                      </span>
                      {item.override?.expiresAt && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded">
                          until {new Date(item.override.expiresAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                      REQUIRED
                    </span>
                  )}
                  {item.reference && (
                    <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                      {item.reference}
                    </span>
                  )}
                </div>
                {/* Quantity Control for Required Items */}
                {item.presence !== 'overridden' && item.qtyRequired > 0 && (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">Qty:</span>
                    <QuantityControl
                      quantity={item.qtyPresent || item.qtyRequired}
                      minQuantity={item.qtyRequired}
                      onChange={(newQty) => onQuantityChange(domain, item.id, newQty, true)}
                      size="sm"
                    />
                  </div>
                )}
              </div>
              {item.description && (
                <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              )}
              {item.override && (
                <div className="mt-1 text-xs text-amber-700">
                  <strong>Override:</strong> {item.override.details}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Abstract requirements removed - all choices made in Step 5 */}

        {/* Wizard-Selected Items */}
        {domainData.wizardSelected?.map(item => (
          <div key={item.id} className="px-6 py-4 flex items-center bg-cyan-50">
            <div className="flex items-center">
              <Switch
                checked={item.selected}
                onChange={(checked) => onItemToggle(domain, item.id, checked)}
                className="mr-3"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-gray-900 font-medium">{item.name}</span>
                  <span className="ml-2 px-2 py-1 bg-cyan-100 text-cyan-800 text-xs font-medium rounded">
                    AUTO-SELECTED
                  </span>
                  {item.reference && (
                    <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                      {item.reference}
                    </span>
                  )}
                </div>
                {/* Quantity Control for Wizard-Selected Items */}
                {item.selected && (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">Qty:</span>
                    <QuantityControl
                      quantity={item.qtyPresent || 1}
                      minQuantity={1}
                      onChange={(newQty) => onQuantityChange(domain, item.id, newQty, false)}
                      size="sm"
                    />
                  </div>
                )}
              </div>
              {item.description && (
                <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              )}
              {item.reason && (
                <p className="text-xs text-cyan-700 mt-1">
                  <span className="font-medium">Source:</span> {item.reason}
                </p>
              )}
            </div>
          </div>
        ))}

        {/* Optional Items */}
        {domainData.optional?.map(item => (
          <div key={item.id} className="px-6 py-4 flex items-center">
            <div className="flex items-center">
              <Switch
                checked={item.selected}
                onChange={(checked) => onItemToggle(domain, item.id, checked)}
                className="mr-3"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-gray-900 font-medium">{item.name}</span>
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                    OPTIONAL
                  </span>
                  {item.reference && (
                    <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                      {item.reference}
                    </span>
                  )}
                </div>
                {/* Quantity Control for Optional Items (only when selected) */}
                {item.selected && (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">Qty:</span>
                    <QuantityControl
                      quantity={item.qtyPresent || 1}
                      minQuantity={1}
                      onChange={(newQty) => onQuantityChange(domain, item.id, newQty, false)}
                      size="sm"
                    />
                  </div>
                )}
              </div>
              {item.description && (
                <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DomainSection;